import React from 'react';
import { array, object, string, bool, oneOf, number } from 'prop-types';
import { types } from '../../../../../util/sdkLoader';

import {
  FormattedMessage,
  intlShape,
  injectIntl,
} from '../../../../../util/reactIntl';
import { displayPrice } from '../../../../../util/configHelpers';
import { convertPrice, formatMoney, convertMoneyToNumber, isNarrowSymbolSupported } from '../../../../../util/currency';
import { withCloudinaryJpgFormat } from '../../../../../util/cloudinary';
import { createSlug, getAlgoliaIndexNameForLang } from '../../../../../util/urlHelpers';
import { isBookingProcessAlias } from '../../../../../transactions/transaction';

import {
  AspectRatioWrapper,
  Button,
  ResponsiveImage,
} from '../../../../../components';
import { useConfiguration } from '../../../../../context/configurationContext';
import { createResourceLocatorString } from '../../../../../util/routes';
import { generateCategorySlug, generateCitySlug } from '../../../../../util/seo';
import css from './ListCard.module.css';
import {
  isPickupRequired,
  getPerDayPriceRange,
  getCustomPackagePrice,
  resolveRatingAndReviews,
} from '../../../../../util/data';
import moment from 'moment';
import { ACCESS_OPTIONS, DELIVERY_FEE, SHIPPING_FEE } from '../../../../../util/constants';
import IconCard from '../../../../../components/SavedCardDetails/IconCard/IconCard';
import DistanceMapModal from './DistanceMapModal';
import RentalRatesSheet from './RentalRatesSheet';
import classNames from 'classnames';
import { loadAlgoliaInsights } from '../../../../../analytics/handlers';

const { UUID } = types;

const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;

const SmallPin = () => (
  <svg width="12" height="13" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 0C3.79 0 2 1.79 2 4c0 3 4 8.5 4 8.5S10 7 10 4c0-2.21-1.79-4-4-4zm0 5.5A1.5 1.5 0 1 1 6 2.5a1.5 1.5 0 0 1 0 3z" fill="#A4A7AE" />
  </svg>
);

const SmallTruck = () => (
  <svg width="14" height="12" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0H1C.45 0 0 .45 0 1v9c0 .55.45 1 1 1h1.17A2.5 2.5 0 0 0 4.5 13a2.5 2.5 0 0 0 2.33-2h5.34A2.5 2.5 0 0 0 14.5 13a2.5 2.5 0 0 0 2.33-2H18c.55 0 1-.45 1-1V6L15 1h-3V0zm0 1.5h2.5L17 5.5V10h-.67A2.5 2.5 0 0 0 14.5 8.5a2.5 2.5 0 0 0-2.33 1.5H6.83A2.5 2.5 0 0 0 4.5 8.5 2.5 2.5 0 0 0 2.17 10H1V1h11v.5zM4.5 10.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm10 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" fill="#A4A7AE" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 4.5L4.5 8L11 1" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StoreVerifiedBadge = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.67101 1.40255C5.73663 1.10008 4.72088 1.52081 4.27405 2.39541L3.73686 3.44686C3.67303 3.57179 3.57143 3.6734 3.44649 3.73723L2.39505 4.27441C1.52045 4.72125 1.09971 5.737 1.40219 6.67137L1.76583 7.79471C1.80903 7.92817 1.80903 8.07191 1.76583 8.20537L1.40219 9.32871C1.09971 10.2631 1.52045 11.2788 2.39505 11.7257L3.44649 12.2628C3.57143 12.3267 3.67303 12.4283 3.73686 12.5532L4.27405 13.6047C4.72088 14.4793 5.73663 14.9 6.67101 14.5976L7.79434 14.2339C7.92781 14.1907 8.07154 14.1907 8.20501 14.2339L9.32834 14.5976C10.2627 14.9 11.2785 14.4793 11.7253 13.6047L12.2625 12.5532C12.3263 12.4283 12.4279 12.3267 12.5529 12.2628L13.6043 11.7257C14.4789 11.2788 14.8997 10.2631 14.5972 9.32871L14.2335 8.20537C14.1903 8.07191 14.1903 7.92817 14.2335 7.79471L14.5972 6.67137C14.8997 5.737 14.4789 4.72125 13.6043 4.27441L12.5529 3.73723C12.4279 3.6734 12.3263 3.57179 12.2625 3.44686L11.7253 2.39541C11.2785 1.52081 10.2627 1.10008 9.32834 1.40255L8.20501 1.76619C8.07154 1.80939 7.92781 1.8094 7.79434 1.76619L6.67101 1.40255ZM4.50619 7.83824L5.449 6.89537L7.33461 8.78104L11.1059 5.00981L12.0487 5.95261L7.33461 10.6666L4.50619 7.83824Z" fill="#101828" />
  </svg>
);

const formatDistance = (distance) => {
  if (!distance || distance?.kilometers === undefined || distance?.miles === undefined) return null;
  return {
    km: distance.kilometers.toFixed(1),
    miles: distance.miles.toFixed(1),
  };
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoneyCompact(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

// Returns up to two initials: first letter of first word + first letter of last word.
const getInitials = name => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0]?.toUpperCase() || '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0]?.toUpperCase() : '';
  return first + last || '?';
};

// Card display only: round to the nearest whole dollar once the price is at least
// $1 (e.g. $5.14 -> $5). Prices under $1 keep their cents (e.g. $0.75) so the
// price isn't rounded away to $0/$1. Underlying price data/calculations are untouched.
const formatMoneyCompact = (intl, money) => {
  const value = convertMoneyToNumber(money);
  if (Math.abs(value) < 1) {
    return formatMoney(intl, money);
  }
  const rounded = Math.round(value);
  const formatted = intl.formatNumber(rounded, {
    style: 'currency',
    currency: money.currency,
    // See src/util/currency.js: 'narrowSymbol' throws a RangeError on Safari 11.1/12.1.
    currencyDisplay: isNarrowSymbolSupported() ? 'narrowSymbol' : 'symbol',
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatted && formatted.replace('US', '');
};

export const ListCardComponent = props => {
  const config = useConfiguration();
  const [isMapModalOpen, setIsMapModalOpen] = React.useState(false);
  const [isRatesSheetOpen, setIsRatesSheetOpen] = React.useState(false);
  const cardRef = React.useRef(null);
  const prefetchTimerRef = React.useRef(null);

  // RAS-2519 long-value fallback. Four-figure prices ("$1,250 to $1,400 /day") overflow
  // the 40px half-width cell at the default 20/13/12 scale. The spec's step 2 is to drop
  // the whole scale to 17/12/10 - together, so the 1.6:1 ratio between the two prices
  // survives - rather than truncating, ellipsising or wrapping mid-number. Whether it
  // overflows depends on the rendered string and the cell's real width, so it has to be
  // measured rather than guessed from a breakpoint.
  const priceBlockRef = React.useRef(null);
  const [isPriceBlockCompact, setIsPriceBlockCompact] = React.useState(false);

  const {
    rental = {},
    intl,
    routes,
    history,
    currentUserCurrency,
    exchangeRates,
    onClick = () => { },
    queryID,
    position,
    onActivateListing,
    onPrefetchListing,
    currentUserCountryCode,
    locale,
    hasLocationAvailable,
    isCritical,
    titleClassName,
    layout = 'grid',
    currentUser,
    startDate,
    endDate,
  } = props;

  const id = rental.objectID;
  const {
    title = '',
    price,
    user: { displayName, publicData: uPublicData },
    publicData,
    distance,
    _geoloc,
    nearest,
  } = rental;

  const defaultReviewCount = config.listing.defaultReviewCount;
  const defaultRating = config.listing.defaultRating;
  const { storeDisplayPhoto } = uPublicData || {};
  const { rating, reviews } = resolveRatingAndReviews(uPublicData, defaultRating, defaultReviewCount);

  const distanceInfo = distance ? formatDistance(distance) : null;

  const {
    category,
    city,
    listingType,
    defaultPriceKey,
    transactionProcessAlias,
    listingImages = [],
    prices,
    access_to_rentals,
    additionalFees,
    location,
  } = publicData || {};

  const address = location?.address;
  const pickupRequired = isPickupRequired(access_to_rentals);

  const isDeliveryAvailable =
    access_to_rentals?.includes(ACCESS_OPTIONS.DELIVERY) ||
    additionalFees?.some(fee => fee.feeType === DELIVERY_FEE);

  const isShippingAvailable =
    access_to_rentals?.includes(ACCESS_OPTIONS.SHIP) ||
    additionalFees?.some(fee => fee.feeType === SHIPPING_FEE);

  const { aspectWidth = 1, aspectHeight = 1 } = config.layout.listingImage;

  const slug = createSlug(title);
  const citySlug = generateCitySlug(city);
  const categorySlug = generateCategorySlug(category);

  const convertedPrice = React.useMemo(() => {
    return !!price
      ? convertPrice(exchangeRates, price.amount, price.currency, currentUserCurrency)
      : null;
  }, [price, exchangeRates, currentUserCurrency]);

  const perDayRange = React.useMemo(
    () => getPerDayPriceRange(prices, exchangeRates, currentUserCurrency),
    [prices, exchangeRates, currentUserCurrency]
  );

  const dateRangePrice = React.useMemo(() => {
    if (!startDate || !endDate || !prices || !exchangeRates?.length) return null;
    const numDays = moment(endDate).diff(moment(startDate), 'days');
    if (numDays <= 0) return null;
    const exactMapping = {
      1: 'perDayPrice',
      2: 'twoDaysPrice',
      3: 'threeDaysPrice',
      4: 'fourDaysPrice',
      5: 'fiveDaysPrice',
      7: 'oneWeekPrice',
      28: 'twentyEightDaysPrice',
    };
    const exactKey = exactMapping[numDays];
    let rawPrice = null;
    if (exactKey && prices[exactKey]?.amount) {
      rawPrice = prices[exactKey];
    } else {
      rawPrice = getCustomPackagePrice(numDays, prices);
    }
    if (!rawPrice) return null;
    return convertPrice(exchangeRates, rawPrice.amount, rawPrice.currency, currentUserCurrency);
  }, [startDate, endDate, prices, exchangeRates, currentUserCurrency]);

  const currentSearchUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';

  const searchParamsObj = {
    ...(queryID && { queryID }),
    from: 'search',
    searchUrl: currentSearchUrl,
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  };

  const firstImage = listingImages[0];
  const firstImageMaybe =
    !!firstImage && firstImage.secure_url ? (
      <ResponsiveImage
        isCritical={isCritical}
        alt={title}
        renderCloudinaryImage={{
          secure_url: firstImage.secure_url,
          alt: title,
          variantsToRender: ['listing-card'],
          defaultVariant: 'listing-card',
        }}
      />
    ) : (
      <div className={css.noImageWrapper}>
        <svg color="#333" width="100px" height="100px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.828 5l-1-1H22v15.172l-1-1v-.69l-3.116-3.117-.395.296-.714-.714.854-.64a.503.503 0 0 1 .657.046L21 16.067V5zM3 20v-.519l2.947-2.947a1.506 1.506 0 0 0 .677.163 1.403 1.403 0 0 0 .997-.415l2.916-2.916-.706-.707-2.916 2.916a.474.474 0 0 1-.678-.048.503.503 0 0 0-.704.007L3 18.067V5.828l-1-1V21h16.172l-1-1zM17 8.5A1.5 1.5 0 1 1 15.5 7 1.5 1.5 0 0 1 17 8.5zm-1 0a.5.5 0 1 0-.5.5.5.5 0 0 0 .5-.5zm5.646 13.854l.707-.707-20-20-.707.707z" />
          <path fill="none" d="M0 0h24v24H0z" />
        </svg>
      </div>
    );

  const navigateToListing = React.useCallback((extraSearchParams = {}, event) => {
    if (event) event.stopPropagation();
    onClick();
    if (typeof window !== 'undefined') {
      const userToken = currentUser?.id?.uuid;
      if (userToken && id) {
        const indexName = getAlgoliaIndexNameForLang(
          localStorage.getItem('appLanguage') || 'en'
        );
        loadAlgoliaInsights().then(aa => {
          if (!aa || typeof aa !== 'function') return;
          if (queryID && position) {
            aa('clickedObjectIDsAfterSearch', {
              userToken,
              eventName: 'Listing Clicked After Search',
              index: indexName,
              objectIDs: [id],
              positions: [position],
              queryID,
            });
          } else {
            aa('clickedObjectIDs', {
              userToken,
              eventName: 'Listing Clicked',
              index: indexName,
              objectIDs: [id],
            });
          }
        });
      }
    }
    history.push(
      createResourceLocatorString(
        'ListingPage',
        routes,
        {
          ...(citySlug ? { city: citySlug } : {}),
          ...(categorySlug ? { category: categorySlug } : {}),
          id,
          slug,
          ...(locale !== 'en' ? { lang: locale } : {}),
        },
        { ...searchParamsObj, ...extraSearchParams }
      )
    );
  }, [citySlug, categorySlug, id, slug, locale, onClick, history, routes, searchParamsObj]);

  const handleDetails = e => navigateToListing({}, e);
  const handleBookNow = e => navigateToListing({}, e);

  const handleSelectDates = e => {
    if (e) e.stopPropagation();
    setIsRatesSheetOpen(false);

    if (startDate && endDate) {
      // Dates already filled: the date row is collapsed in the sticky bar
      // (desktop and mobile both collapse filled rows once pinned). Fire a
      // custom event so the active search bar expands it in place — no scroll needed.
      window.dispatchEvent(new CustomEvent('ras:openDatePicker'));
    } else {
      // Dates not filled: the date row is already visible.
      // Just focus the input; give the rates sheet 150ms to finish closing first.
      const isDesktopBar = typeof window !== 'undefined' && window.innerWidth >= 1024;
      const inputId = isDesktopBar ? 'desktopStartDate' : 'algoliaStartDate';
      setTimeout(() => {
        const el = document.getElementById(inputId);
        if (el) { el.focus(); el.click(); }
      }, 150);
    }
  };

  const handleDistanceClick = (e) => {
    e.stopPropagation();
    if (onActivateListing) onActivateListing(new UUID(id));
    if (!isDesktop) setIsMapModalOpen(true);
  };

  const setActivePropsMaybe = onActivateListing
    ? {
      onMouseEnter: () => {
        onActivateListing(new UUID(id));
        if (onPrefetchListing) {
          clearTimeout(prefetchTimerRef.current);
          prefetchTimerRef.current = setTimeout(() => {
            onPrefetchListing(new UUID(id));
          }, 200);
        }
      },
      onMouseLeave: () => {
        onActivateListing(null);
        clearTimeout(prefetchTimerRef.current);
      },
    }
    : null;

  const numDays = startDate && endDate ? moment(endDate).diff(moment(startDate), 'days') : 0;

  // For carousel (landing page), check viewport at render time so CSS and JS stay in sync.
  // Module-level `isDesktop` can be stale if the module loaded before the viewport was finalised.
  const isCarousel = layout === 'carousel';
  const isDesktopViewport = isCarousel
    ? (typeof window !== 'undefined' && window.innerWidth > 768)
    : isDesktop;
  const showDesktopPills = isCarousel ? isDesktopViewport : isDesktop;

  // RAS-2519: the price block is built from explicitly sized fragments rather than one
  // formatted string, so the lead price, the second price and the unit can each carry
  // their own size/weight. Splitting the text across nodes would normally make a screen
  // reader announce it with hard stops ("21. to 23. per day"), so the container carries
  // an aria-label with the whole phrase and the fragments are hidden from the a11y tree.
  const perDayUnitLabel = intl.formatMessage({ id: 'ListCard.perDayLabel' });

  const priceBlock = dateRangePrice ? (
    // Dates selected. Content is unchanged from before this ticket (price · N days) -
    // only the type treatment changes. The day count is not a price, so it is grey.
    {
      ariaLabel: intl.formatMessage(
        { id: 'ListCard.priceAriaDates' },
        { price: formatMoneyCompact(intl, dateRangePrice), dayCount: numDays }
      ),
      content: (
        <>
          <span className={css.priceLead}>{formatMoneyCompact(intl, dateRangePrice)}</span>
          <span className={css.priceLabel}>
            <FormattedMessage
              id="ListCard.priceRentalDays"
              values={{ dayCount: numDays }}
            />
          </span>
        </>
      ),
    }
  ) : perDayRange ? (
    perDayRange.isSingleTier
      ? {
        // Single-rate listing: one price and its unit. The "to $XX" fragment is not
        // rendered at all - no empty node to collapse or leave an orphaned "to".
        ariaLabel: intl.formatMessage(
          { id: 'ListCard.priceAriaSingle' },
          { price: formatMoneyCompact(intl, perDayRange.minPrice) }
        ),
        content: (
          <>
            <span className={css.priceLead}>
              {formatMoneyCompact(intl, perDayRange.minPrice)}
            </span>
            <span className={css.priceUnit}>{perDayUnitLabel}</span>
          </>
        ),
      }
      : {
        // Browse state. The word "to" rather than a dash: a dash reads as a span to
        // skim past, the word slows the eye enough to register both ends.
        ariaLabel: intl.formatMessage(
          { id: 'ListCard.priceAriaBrowse' },
          {
            min: formatMoneyCompact(intl, perDayRange.minPrice),
            max: formatMoneyCompact(intl, perDayRange.maxPrice),
          }
        ),
        content: (
          <>
            <span className={css.priceLead}>
              {formatMoneyCompact(intl, perDayRange.minPrice)}
            </span>
            <span className={css.priceSecond}>
              <FormattedMessage
                id="ListCard.priceRangeTo"
                values={{ max: formatMoneyCompact(intl, perDayRange.maxPrice) }}
              />
            </span>
            <span className={css.priceUnit}>{perDayUnitLabel}</span>
          </>
        ),
      }
  ) : convertedPrice ? (
    {
      ariaLabel: priceData(convertedPrice, currentUserCurrency, intl).formattedPrice,
      content: (
        <span className={css.priceLead}>
          {priceData(convertedPrice, currentUserCurrency, intl).formattedPrice}
        </span>
      ),
    }
  ) : null;

  const priceRangeDisplay = priceBlock?.content ?? null;

  // Measure at the default scale first, then step down only if the content genuinely
  // doesn't fit. Re-measures on resize and whenever the rendered price text changes
  // (currency switch, dates picked/cleared), and always re-tests at full size so a card
  // that no longer needs the smaller scale returns to it.
  const priceBlockAriaLabel = priceBlock?.ariaLabel;
  React.useLayoutEffect(() => {
    const el = priceBlockRef.current;
    const inner = el?.firstElementChild;
    if (!el || !inner) {
      return;
    }

    const measure = () => {
      const available = el.clientWidth - 20; // .priceRange padding: 0 10px
      // scrollWidth of the inner span is the content's natural width at whichever scale
      // is currently applied, so compare at the default scale by clearing compact first.
      setIsPriceBlockCompact(false);
      requestAnimationFrame(() => {
        if (!priceBlockRef.current) return;
        const naturalWidth = inner.scrollWidth;
        setIsPriceBlockCompact(naturalWidth > available);
      });
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [priceBlockAriaLabel]);

  const hasBadges = pickupRequired || isDeliveryAvailable || isShippingAvailable;

  const distanceMsgId = nearest === true ? 'ListCard.nearest' : 'ListCard.distance';
  const distanceMsgIdMobile = nearest === true ? 'ListCard.nearestMobile' : 'ListCard.distance';
  const distanceValues = {
    distance: currentUserCountryCode === 'US' ? distanceInfo?.miles : distanceInfo?.km,
    unit: currentUserCountryCode === 'US' ? 'miles' : 'km',
  };

  return (
    <div
      className={classNames(
        css.listCard,
        layout === 'grid' ? css.layoutGrid : css.layoutCarousel
      )}
      data-testid="listing-card"
      onClick={handleDetails}
      ref={cardRef}
      {...setActivePropsMaybe}
    >
      {/* ── Header: image + overlaid elements (desktop) / image + info column (mobile) ── */}
      <div className={css.header}>

        {/* Desktop-only overlay: distance badge pinned to top-left of image */}
        {distanceInfo && hasLocationAvailable && (
          <div className={css.imageTopOverlay}>
            <div
              className={classNames(css.distance, css.distanceOnImage, css.distanceWithArea, nearest === true && css.nearestDistance)}
              onClick={handleDistanceClick}
            >
              <IconCard brand="location" />
              <span className={css.distanceLabel}>
                <FormattedMessage id={distanceMsgId} values={distanceValues} />
              </span>
              {city && <span className={css.distanceCity}>&nbsp;({city})</span>}
            </div>
          </div>
        )}

        <AspectRatioWrapper
          className={classNames(css.aspectRatioWrapper, 'aspect-ratio-wrapper')}
          width={aspectWidth}
          height={aspectHeight}
        >
          {firstImageMaybe}
        </AspectRatioWrapper>

        {/* Info column (visible on both mobile and desktop) */}
        <div className={css.deliveryInfoTitleWrapper}>
          {/* Distance badge — mobile only (hidden on desktop via CSS) */}
          {distanceInfo && hasLocationAvailable && (
            <div className={css.nearestContainer}>
              <div
                className={classNames(css.distance, css.distanceWithArea, nearest === true && css.nearestDistance)}
                onClick={handleDistanceClick}
              >
                <IconCard brand="location" />
                <span className={css.distanceLabel}>
                  <FormattedMessage id={distanceMsgIdMobile} values={distanceValues} />
                </span>
                {city && <span className={css.distanceCity}>&nbsp;({city})</span>}
              </div>
            </div>
          )}

          <div className={classNames(css.title, titleClassName)}>{title}</div>

          {(city || pickupRequired || isDeliveryAvailable || isShippingAvailable) && (
            <div className={css.accessInfoStack}>
              {showDesktopPills ? (
                <>
                  {(pickupRequired || city) && (
                    <div className={css.deliveryInfo}>
                      <SmallPin />
                      <span>
                        {city ? (
                          <FormattedMessage id="ListCard.pickupInCity" values={{ city }} />
                        ) : (
                          <FormattedMessage id="ListCard.pickupAtOwnerLocation" />
                        )}
                      </span>
                    </div>
                  )}
                  {isDeliveryAvailable && (
                    <div className={css.deliveryInfo}>
                      <SmallTruck />
                      <span><FormattedMessage id="ListCard.deliveryAvailable" /></span>
                    </div>
                  )}
                  {isShippingAvailable && (
                    <div className={css.deliveryInfo}>
                      <CheckIcon />
                      <span><FormattedMessage id="ListCard.shippingAvailable" /></span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {(pickupRequired || city) && (
                    <div className={css.accessInfoLine}>
                      <SmallPin />
                      <span>
                        {city ? (
                          <FormattedMessage id="ListCard.pickupInCity" values={{ city }} />
                        ) : (
                          <FormattedMessage id="ListCard.pickupAtOwnerLocation" />
                        )}
                      </span>
                    </div>
                  )}
                  {isDeliveryAvailable && (
                    <div className={css.accessInfoLine}>
                      <SmallTruck />
                      <span><FormattedMessage id="ListCard.deliveryAvailable" /></span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Price range + Rent Now ── */}
      {priceRangeDisplay && (
        <div className={css.priceActionRow}>
          <div
            ref={priceBlockRef}
            className={classNames(css.priceRange, {
              [css.priceRangeCompact]: isPriceBlockCompact,
            })}
            // Read as one continuous phrase ("21 to 23 dollars per day") rather than the
            // separate nodes that produce the size hierarchy, which a screen reader would
            // otherwise announce with hard stops between fragments.
            aria-label={priceBlock?.ariaLabel}
            onClick={isCarousel ? handleBookNow : (e => { e.stopPropagation(); setIsRatesSheetOpen(true); })}
          >
            <span aria-hidden="true" className={css.priceBlockInner}>
              {priceRangeDisplay}
            </span>
          </div>
          <Button
            type="button"
            onClick={isCarousel ? handleBookNow : (startDate && endDate ? handleBookNow : handleSelectDates)}
            className={classNames(css.rentNowButton, isCarousel && css.rentNowButtonCarousel)}
          >
            {isCarousel || (startDate && endDate)
              ? <FormattedMessage id="ListCard.rentNow" />
              : <FormattedMessage id="ListCard.selectDates" />
            }
          </Button>
        </div>
      )}

      {/* ── Store info + Rating ── */}
      <div className={css.cardBottom}>
        <div className={css.storeInfoRow}>
          <div className={css.storeAvatarWrapper}>
            <div className={css.storeAvatarInner}>
              {storeDisplayPhoto?.secure_url ? (
                <img src={withCloudinaryJpgFormat(storeDisplayPhoto.secure_url)} alt={displayName} className={css.storeAvatarImg} />
              ) : (
                <span className={css.storeAvatarInitials}>{getInitials(displayName)}</span>
              )}
            </div>
            <span className={css.storeVerifiedDot}>
              <StoreVerifiedBadge size={14} />
            </span>
          </div>
          <div className={css.storeNameRatingGroup}>
            {displayName && (
              <div className={css.storeDisplayName}>{displayName}</div>
            )}
            <div className={css.ratingRow}>
              <span className={css.ratingRowVerifiedBadge}><StoreVerifiedBadge size={14} /></span>
              <IconCard brand="star" />
              <span className={css.ratingText}>{rating.toFixed(1)}</span>
              <span className={css.reviewCount}>
                <FormattedMessage id="ListCard.reviews" values={{ reviews }} />
              </span>
            </div>
          </div>
        </div>

        {/* Mobile-only: store name, set apart from the rating row above */}
        {displayName && (
          <div className={css.byStoreMobile}>
            <FormattedMessage id="ListCard.byStore" values={{ storeName: displayName }} />
          </div>
        )}
      </div>

      {!isDesktop && (
        <DistanceMapModal
          isOpen={isMapModalOpen}
          onClose={(e) => {
            e.stopPropagation();
            setIsMapModalOpen(false);
          }}
          geolocation={_geoloc}
          address={address}
          title={title}
        />
      )}

      <RentalRatesSheet
        isOpen={isRatesSheetOpen}
        onClose={() => setIsRatesSheetOpen(false)}
        title={title}
        prices={prices}
        exchangeRates={exchangeRates}
        selectedCurrency={currentUserCurrency}
        intl={intl}
        onViewListing={handleDetails}
        onSelectDates={handleSelectDates}
        startDate={startDate}
        endDate={endDate}
        dateRangePrice={dateRangePrice}
        numDays={numDays}
      />
    </div>
  );
};

ListCardComponent.propTypes = {
  intl: intlShape.isRequired,
  rental: object.isRequired,
  currentUserCurrency: string.isRequired,
  exchangeRates: array.isRequired,
  isCritical: bool.isRequired,
  layout: oneOf(['grid', 'carousel']),
  queryID: string,
  position: number,
  currentUser: object,
};

export default injectIntl(ListCardComponent);
